// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { usePlanSettingsStyles } from "../PlanSettings.styles";
// Utilities
import { format } from "@fluentui/utilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import * as React from "react";
/**
 * BackgroundOption component for displaying a single background image option
 *
 * This component renders an individual background image thumbnail that can be selected.
 * It handles keyboard navigation and accessibility attributes.
 *
 * @param props - Component properties
 * @returns JSX element representing a single background option
 */ export const BackgroundOption = /*#__PURE__*/ React.forwardRef(({ suggestion, index, isSelected, tabIndex, onSelect }, ref)=>{
    const styles = usePlanSettingsStyles();
    // Prefer the image's alt text as the option name; fall back to the generic position label when it's empty.
    const trimmedAltText = suggestion.altText?.trim();
    const ariaLabel = trimmedAltText || format(Strings.PlanSettings_SuggestedBackgroundAriaLabel, (index + 1).toString());
    /**
         * Handle keyboard interactions for selecting the background
         */ const handleKeyDown = (e)=>{
        const key = KeyboardUtilities.getKey(e);
        if (key === KeyCode.ENTER || key === KeyCode.SPACE) {
            onSelect(suggestion.fullUrl);
            e.preventDefault();
        }
    };
    return /*#__PURE__*/ React.createElement("div", {
        ref: ref,
        "data-testid": "background-option",
        className: `${styles.backgroundOption} ${isSelected ? styles.selected : ""}`,
        onClick: ()=>onSelect(suggestion.fullUrl),
        onKeyDown: handleKeyDown,
        tabIndex: tabIndex,
        role: "option",
        "aria-label": ariaLabel,
        "aria-selected": isSelected
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.backgroundThumbnail
    }, /*#__PURE__*/ React.createElement("img", {
        src: suggestion.thumbnailUrl,
        alt: "",
        "aria-hidden": "true",
        className: styles.backgroundImage
    }), /*#__PURE__*/ React.createElement("svg", {
        className: styles.backgroundOverlay,
        width: "244",
        height: "137",
        viewBox: "0 0 244 137",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        focusable: "false",
        "aria-hidden": "true"
    }, /*#__PURE__*/ React.createElement("rect", {
        x: "37.8457",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "37.8457",
        y: "41.9585",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "37.8457",
        y: "82.272",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "82.2729",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "82.2729",
        y: "41.9585",
        width: "33.7318",
        height: "18.9227",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "82.2729",
        y: "66.6401",
        width: "33.7318",
        height: "46.8954",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "126.7",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "126.7",
        y: "41.9585",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "126.7",
        y: "82.272",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "171.127",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "171.127",
        y: "41.9585",
        width: "33.7318",
        height: "18.9227",
        rx: "3",
        fill: "white"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "171.127",
        y: "66.6401",
        width: "33.7318",
        height: "46.8954",
        rx: "3",
        fill: "white"
    }))));
});
BackgroundOption.displayName = "BackgroundOption";
